import { FC, useState } from 'react';
import { FormInstance, Table } from 'antd';
import { isMobileOnly } from 'react-device-detect';
import i18n from 'i18next';

import {
	downloadCurrentInvestments,
	getMyCurrentInvestments,
} from 'features/myInvestments/actions';

import { useAppSelector, useMyInvestmentTable } from 'hooks';

import { ColumnTypes, FieldData, IFilterValues } from 'models';

import { PAGE_SIZE, RequestStatus } from 'utils/constants';

import { RejectedMessage, TableValue, TableWrapper } from 'componets/ui';
import { MyInvestmentsFilters } from '../MyInvestmentsFilters';

import { cartIconColumn, columnsTable, receivedPaymentColumns } from '../columnsTable';

const columns = [
	...columnsTable,
	{
		title: i18n.t('remainingAmountText'),
		dataIndex: 'remainingPrincipal',
		width: 125,
		render: (amount: number) => <TableValue value={amount} isAmount />,
	},
	{
		title: i18n.t('nextPaymentDateText'),
		dataIndex: 'nextPaymentDate',
		width: 125,
		sorter: true,
		render: (date: string | null) => <TableValue value={date} />,
	},
	{
		title: i18n.t('nextPaymentAmountText'),
		dataIndex: 'estimatedNextPayment',
		width: 125,
		render: (amount: number) => <TableValue isAmount value={amount} />,
	},
	{
		title: i18n.t('estimatedPaymentDateText'),
		dataIndex: 'estimatedFinalPaymentDate',
		width: 140,
		sorter: true,
		render: (date: string | null) => <TableValue value={date} />,
	},
	{
		title: i18n.t('accumulatedInterestText'),
		dataIndex: 'totalPenalties',
		width: 130,
		render: (amount: number) => <TableValue isAmount value={amount} isPenalties />,
	},
	...receivedPaymentColumns,
	cartIconColumn,
];

type FundedProjectsProps = {
	initialValues: IFilterValues;
	form: FormInstance;
	values: IFilterValues;
	type: string;
};

export const FundedProjects: FC<FundedProjectsProps> = ({ initialValues, form, values, type }) => {
	const {
		myInvestments,
		pager,
		status: requestStatus,
	} = useAppSelector((state) => state.myInvestments);

	const [fields, setFields] = useState<FieldData[]>([]);

	const { page, onDownload, onChange } = useMyInvestmentTable({
		values,
		initialValueSort: '-dateOfPurchase',
		isGet: type === 'fundedProjects',
		downloadCallback: downloadCurrentInvestments,
		getCallback: getMyCurrentInvestments,
	});

	const isPending = requestStatus === RequestStatus.PENDING;

	return (
		<div>
			<MyInvestmentsFilters
				length={myInvestments.length}
				onDownload={onDownload}
				form={form}
				fields={fields}
				setFields={setFields}
				initialValues={initialValues}
				values={values}
			/>

			{requestStatus === RequestStatus.REJECTED ? (
				<RejectedMessage />
			) : (
				<TableWrapper data={myInvestments} loading={isPending && myInvestments.length === 0}>
					<Table
						loading={isPending}
						onChange={onChange}
						className="accountTable myInvestmentsTable"
						scroll={{ x: 1850 }}
						columns={columns as ColumnTypes}
						dataSource={myInvestments}
						showSorterTooltip={false}
						sortDirections={['ascend', 'descend', 'ascend']}
						pagination={{
							current: page,
							defaultPageSize: PAGE_SIZE,
							total: pager.totalCount,
							showSizeChanger: false,
							position: isMobileOnly ? ['bottomCenter'] : ['bottomRight'],
							hideOnSinglePage: true,
							showLessItems: true,
						}}
					/>
				</TableWrapper>
			)}
		</div>
	);
};
